import { useState } from 'react';
import { Alert, Button, Group, Select, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { Link, useNavigate } from 'react-router';
import { RISK_METODOLOJI_ETIKETI } from '@isg/validation';
import {
  useEksikRiskDegerlendirmesi,
  useRiskBantlari,
  useRiskDegerlendirmeleri,
  useRiskOptions,
} from '../api/queries';
import type { RiskListeSuzgeci } from '../api/queries';
import { BosListeNotu, QueryStatus } from './parts';
import { KapsamMetni } from './risk-parts';
import { Rozet, Sayfalama, SuzgecGostergesi } from '../sozlesme';
import { ANLAM } from '../theme';
import { tarihGoster } from '../lib/format';

/**
 * Risk değerlendirmeleri listesi (F3B·S9 — Kapsam §16.12).
 *
 * Üç TÜRETİLMİŞ işaret sunucudan gelir ve ekran bunları YENİDEN HESAPLAMAZ
 * (`kazaDosyasiEksikMi` deseni): **vade doldu** · **imzadan beri değişti** ·
 * **yüksek bantta aksiyonsuz madde sayısı**.
 *
 * ✏️ **MADDE 220 (11.09.2026; Kapsam §16.12/16-g) — SÖZLEŞMEYE HİZALANDI:**
 * - **R6 — KAYIT MODALDA AÇILMAZ** (§20.3b): *"Yeni değerlendirme"* boş
 *   tablo sayfasına (`/risk/yeni`) gider; künye orada, sayfanın üstünde
 *   doldurulur. Modal ve onun durum alanları kalktı.
 * - **P3 — rozetler sözleşmenin `Rozet`inden** (ham `Badge` yoktu değil,
 *   vardı; ekranda renk anlamı tek yerden).
 * - **M1 — kavram cümlesi kalktı** (ekran cümle envanteri satır 92): yöntem
 *   bilgisi artık künyede, seçildiği yerde. Kalan tek metin **bant tablosu
 *   tanımsız UYARISIDIR** — bir açıklama değil, bir durum.
 */
export function RiskDegerlendirmeleriScreen(): React.JSX.Element {
  // R7 — süzgeç ve sayfa SUNUCUDA; ekran ikinci bir filtre yazmaz.
  const [suzgec, setSuzgec] = useState<RiskListeSuzgeci>({});
  const [aramaMetni, setAramaMetni] = useState('');
  const liste = useRiskDegerlendirmeleri(suzgec);
  const options = useRiskOptions();
  const bantlar = useRiskBantlari();
  // "Değerlendirmesi yok" İŞARETİ (F4·S1 — §22.2/9 tip 6'nın detay yüzeyi).
  const eksikIsyerleri = useEksikRiskDegerlendirmesi();
  const navigate = useNavigate();

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Risk değerlendirmeleri</Title>
        {/* Yetki SUNUCUDAN (`kayitAcabilir` = risk_degerlendirme üyeliği). */}
        {options.data?.kayitAcabilir && (
          <Button component={Link} to="/risk/yeni" data-testid="risk-ac">
            Yeni değerlendirme
          </Button>
        )}
      </Group>

      {bantlar.data && !bantlar.data.tanimliMi && (
        <Alert color={ANLAM.dikkat} data-testid="risk-bant-tanimsiz">
          {RISK_METODOLOJI_ETIKETI[bantlar.data.metodoloji]} için bant sınırları tanımlanmamış —
          risk seviyesi hesaplanamıyor.
        </Alert>
      )}

      {/* R7 — arama + süzgeç SUNUCUDA; toplam ve sayfa her zaman görünür (P4). */}
      <Group align="flex-end">
        <TextInput
          label="Ara"
          placeholder="Değerlendirme · faaliyet · işyeri"
          value={aramaMetni}
          onChange={(e) => setAramaMetni(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') setSuzgec((m) => ({ ...m, q: aramaMetni, sayfa: 1 }));
          }}
          data-testid="risk-arama"
        />
        <Button
          variant="default"
          data-testid="risk-ara"
          onClick={() => setSuzgec((m) => ({ ...m, q: aramaMetni, sayfa: 1 }))}
        >
          Ara
        </Button>
        <Select
          label="Durum"
          data={[
            { value: 'aktif', label: 'Aktif' },
            { value: 'arsivlenmis', label: 'Arşivlenmiş' },
          ]}
          value={suzgec.durum ?? null}
          onChange={(v) =>
            setSuzgec((m) => ({
              ...m,
              durum: v === 'aktif' || v === 'arsivlenmis' ? v : null,
              sayfa: 1,
            }))
          }
          clearable
          data-testid="risk-durum-suzgeci"
        />
        <SuzgecGostergesi
          etkinSayisi={(suzgec.q?.trim() ? 1 : 0) + (suzgec.durum ? 1 : 0)}
          onTemizle={() => {
            setAramaMetni('');
            setSuzgec({});
          }}
          testId="risk-suzgec-gostergesi"
        />
      </Group>

      {(eksikIsyerleri.data?.length ?? 0) > 0 && (
        <Alert
          color={ANLAM.dikkat}
          title="Risk değerlendirmesi olmayan işyerleri"
          data-testid="risk-eksik-isyerleri"
        >
          <Text size="sm">
            Aşağıdaki aktif işyerlerinde <b>yayınlanmış</b> (sürümü dondurulmuş) risk
            değerlendirmesi yok:{' '}
            {(eksikIsyerleri.data ?? [])
              .map((isyeri) => `${isyeri.companyAd} — ${isyeri.ad}`)
              .join(' · ')}
          </Text>
        </Alert>
      )}

      <QueryStatus queries={[liste, options]} />
      {liste.data && liste.data.toplam === 0 && (
        <BosListeNotu
          mesaj={
            suzgec.q?.trim() || suzgec.durum
              ? 'Bu süzgeçle eşleşen risk değerlendirmesi yok.'
              : 'Henüz risk değerlendirmesi yok.'
          }
        />
      )}
      {liste.data && liste.data.toplam > 0 && (
        <>
          <Table striped highlightOnHover data-testid="risk-liste">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Değerlendirme</Table.Th>
                <Table.Th>İşyeri</Table.Th>
                <Table.Th>Kapsam</Table.Th>
                <Table.Th>Yöntem</Table.Th>
                <Table.Th>Madde</Table.Th>
                <Table.Th>Son sürüm</Table.Th>
                <Table.Th>Durum</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {liste.data.satirlar.map((r) => (
                <Table.Tr
                  key={r.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => void navigate(`/risk/${r.id}`)}
                  data-testid="risk-liste-satiri"
                >
                  <Table.Td>{r.ad}</Table.Td>
                  <Table.Td>{r.workplaceAd}</Table.Td>
                  <Table.Td>
                    <KapsamMetni
                      kapsamTipi={r.kapsamTipi}
                      locationAd={r.locationAd}
                      faaliyetAdi={r.faaliyetAdi}
                    />
                  </Table.Td>
                  <Table.Td>{RISK_METODOLOJI_ETIKETI[r.metodoloji]}</Table.Td>
                  <Table.Td>{r.maddeSayisi}</Table.Td>
                  <Table.Td>
                    {r.sonSurumNo === null ? (
                      <Text size="sm" c="dimmed">
                        imzalanmadı
                      </Text>
                    ) : (
                      <Text size="sm">
                        v{r.sonSurumNo} · {tarihGoster(r.sonDondurma)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      {r.durum === 'arsivlenmis' && <Rozet anlam="bilgi">arşiv</Rozet>}
                      {r.vadesiDoldu && (
                        <Rozet anlam="gecikmis" testId="risk-vade-doldu">
                          yenileme vadesi doldu
                        </Rozet>
                      )}
                      {r.imzadanBeriDegisti && (
                        <Rozet anlam="dikkat" testId="risk-imzadan-beri-degisti">
                          imzadan beri değişti
                        </Rozet>
                      )}
                      {r.yuksekBantAksiyonsuz > 0 && (
                        <Rozet anlam="dikkat" testId="risk-aksiyonsuz">
                          {r.yuksekBantAksiyonsuz} madde aksiyonsuz
                        </Rozet>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={liste.data.sayfa}
            sayfaBoyutu={liste.data.sayfaBoyutu}
            toplam={liste.data.toplam}
            onDegis={(sayfa) => setSuzgec((m) => ({ ...m, sayfa }))}
            testId="risk-sayfalama"
          />
        </>
      )}
    </Stack>
  );
}
